<template>
  <div class="appFormShell">
    <el-card class="card linkFormSection" data-section-id="basic">
      <div slot="header" class="linkFormSection__header">
        <strong class="linkFormSection__title">基本信息</strong>
      </div>

      <div class="linkFormBasic">
        <div class="linkFormBasic__media">
          <div class="linkFormMediaAnchor">
            <el-image
              v-if="ruleForm.icon"
              class="linkFormMediaAnchor__preview"
              :src="ruleForm.icon"
              :preview-src-list="[ruleForm.icon]"
              fit="cover"
            >
              <div slot="error" class="linkFormMediaAnchor__previewFallback">
                <i class="el-icon-mobile-phone" aria-hidden="true" />
              </div>
            </el-image>
            <button
              v-else
              type="button"
              class="linkFormUploadZone linkFormUploadZone--anchor"
              :disabled="uploadLoading"
              @click="triggerIconUpload"
            >
              <i class="el-icon-upload2 linkFormUploadZone__icon" aria-hidden="true" />
              <span>上传 Icon</span>
              <span class="linkFormUploadZone__hint">建议 512×512，支持 .jpg / .png / .webp</span>
            </button>
          </div>

          <div v-if="ruleForm.icon" class="linkFormMediaFileBar">
            <span class="linkFormMediaFileBar__name">应用 Icon</span>
            <div class="linkFormMediaFileBar__actions">
              <button
                type="button"
                class="linkFormMediaFileBar__btn"
                :disabled="uploadLoading"
                @click="triggerIconUpload"
              >
                替换
              </button>
              <button
                type="button"
                class="linkFormMediaFileBar__btn linkFormMediaFileBar__btn--danger"
                @click="clearIcon"
              >
                移除
              </button>
            </div>
          </div>

          <el-upload
            ref="iconUpload"
            class="appIconUploadHidden"
            action="111"
            accept=".jpg,.jpeg,.png,.webp"
            :http-request="customRequest"
            :show-file-list="false"
          />

          <el-form-item prop="icon" class="linkFormBasic__urlField">
            <el-input v-show="false" v-model="ruleForm.icon" />
          </el-form-item>
        </div>

        <div class="linkFormBasic__fields appFormShell__basicFields">
          <div class="appFormGrid appFormGrid--two appFormShell__nameRow">
            <el-form-item label="应用名称" prop="appName" class="appFormShell__nameField">
              <el-input v-model="ruleForm.appName" placeholder="请输入应用名称" />
            </el-form-item>
            <el-form-item label="appKey" prop="appKey">
              <el-input v-model="ruleForm.appKey" placeholder="请输入 appKey" />
            </el-form-item>
          </div>
        </div>
      </div>
    </el-card>

    <el-card class="card linkFormSection">
      <div slot="header" class="linkFormSection__header">
        <strong class="linkFormSection__title">域名与包名</strong>
      </div>

      <p class="appFormShell__hint">
        domainName 与 cdnPrefix 联动，修改一侧会自动同步另一侧。
      </p>

      <div class="appFormGrid appFormGrid--two">
        <el-form-item label="domainName" prop="domainName">
          <el-select
            v-model="ruleForm.domainName"
            placeholder="请选择网址域名"
            class="formItemStyle"
            popper-class="linkListFilterDropdown"
            @change="handleDomainChange(true)"
          >
            <el-option
              v-for="item in domainNameOptions"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="cdnPrefix" prop="cdnPrefix">
          <el-select
            v-model="ruleForm.cdnPrefix"
            placeholder="请选择 CDN 域名"
            class="formItemStyle"
            popper-class="linkListFilterDropdown"
            @change="handleDomainChange(false)"
          >
            <el-option
              v-for="item in cdnPrefixOptions"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
      </div>

      <div class="appFormShell__packageBlock">
        <div class="appFormShell__labelRow">
          <span class="appFormShell__fieldLabel">包名</span>
          <button type="button" class="appPackageListField__addBtnInline" @click="addPackage">
            <i class="el-icon-plus" aria-hidden="true" />
            添加包名
          </button>
        </div>
        <app-package-list-field
          :package-list.sync="ruleForm.packageList"
          hide-add-button
        />
      </div>
    </el-card>

    <el-card class="card linkFormSection">
      <div slot="header" class="linkFormSection__header">
        <strong class="linkFormSection__title">平台与发布</strong>
      </div>

      <div class="appFormGrid appFormGrid--two appFormShell__platformRow">
        <el-form-item label="平台" prop="platform">
          <style-form-choice
            v-model="ruleForm.platform"
            :options="platformFormOptions"
            aria-label="平台"
            @change="handlePlatformChange"
          />
        </el-form-item>

        <el-form-item label="返回图片格式">
          <style-form-choice
            v-model="ruleForm.pictureFormat"
            :options="visiblePictureFormatOptions"
            aria-label="返回图片格式"
          />
          <p v-if="isCommonFormatDisabled" class="linkFormBasic__keyHint">
            Android 新建应用默认使用 WebP。
          </p>
        </el-form-item>
      </div>

      <el-form-item label="商店链接">
        <el-input v-model="ruleForm.storeLink" placeholder="App Store / Google Play 链接（选填）" />
      </el-form-item>
    </el-card>

    <el-card class="card linkFormSection">
      <div slot="header" class="linkFormSection__header">
        <strong class="linkFormSection__title">数据集字段</strong>
      </div>

      <el-form-item prop="dynamicTags" class="appFormShell__datasetItem">
        <div class="appDatasetField" :class="{ 'appDatasetField--focused': tagInputFocused }">
          <el-tag
            v-for="tag in ruleForm.dynamicTags"
            :key="tag"
            closable
            class="appDatasetField__pill"
            :disable-transitions="false"
            @close="removeDatasetTag(tag)"
          >
            {{ tag }}
          </el-tag>
          <input
            v-model="inputValue"
            class="appDatasetField__input"
            type="text"
            :placeholder="ruleForm.dynamicTags.length > 0 ? '' : '数据字段'"
            aria-label="数据集字段"
            @keyup.enter="handleInputConfirm"
            @focus="tagInputFocused = true"
            @blur="tagInputFocused = false"
          />
        </div>
      </el-form-item>
    </el-card>

    <el-card class="card linkFormSection">
      <div slot="header" class="linkFormSection__header">
        <strong class="linkFormSection__title">其他</strong>
      </div>
      <el-form-item label="备注（选填）">
        <el-input
          v-model="ruleForm.desc"
          type="textarea"
          :rows="3"
          placeholder="运营备注"
        />
      </el-form-item>
    </el-card>
  </div>
</template>

<script>
import { uploadFile } from '../../../api/comment'
import AppPackageListField from './AppPackageListField.vue'
import StyleFormChoice from '../../collectionManage/styleManage/StyleFormChoice.vue'
import {
  CDN_PREFIX_OPTIONS,
  DOMAIN_NAME_OPTIONS,
  PICTURE_FORMAT_OPTIONS,
  PLATFORM_FORM_OPTIONS,
  syncCdnFromDomain,
  syncDomainFromCdn,
} from './appFormConstants'

export default {
  name: 'AppFormShell',
  components: {
    AppPackageListField,
    StyleFormChoice,
  },
  props: {
    ruleForm: { type: Object, required: true },
    isCreate: { type: Boolean, default: true },
  },
  data() {
    return {
      domainNameOptions: DOMAIN_NAME_OPTIONS,
      cdnPrefixOptions: CDN_PREFIX_OPTIONS,
      pictureFormatOptions: PICTURE_FORMAT_OPTIONS,
      platformFormOptions: PLATFORM_FORM_OPTIONS,
      inputValue: '',
      tagInputFocused: false,
      isCommonFormatDisabled: false,
      uploadLoading: false,
    }
  },
  computed: {
    visiblePictureFormatOptions() {
      if (this.isCommonFormatDisabled) {
        return this.pictureFormatOptions.filter((item) => item.value === '2')
      }
      return this.pictureFormatOptions
    },
  },
  watch: {
    isCreate: {
      immediate: true,
      handler() {
        this.syncFormatLockFromPlatform()
      },
    },
    'ruleForm.platform'() {
      this.syncFormatLockFromPlatform()
    },
  },
  methods: {
    syncFormatLockFromPlatform() {
      if (this.isCreate && this.ruleForm.platform === 'Android') {
        this.isCommonFormatDisabled = true
        if (this.ruleForm.pictureFormat !== '2') {
          this.ruleForm.pictureFormat = '2'
        }
      } else if (this.isCreate) {
        this.isCommonFormatDisabled = false
      } else {
        this.isCommonFormatDisabled = false
      }
    },
    handlePlatformChange(val) {
      if (this.isCreate && val === 'Android') {
        this.ruleForm.pictureFormat = '2'
        this.isCommonFormatDisabled = true
      } else if (this.isCreate) {
        this.isCommonFormatDisabled = false
      }
    },
    handleInputConfirm() {
      const inputValue = String(this.inputValue || '').trim()
      if (inputValue) {
        this.ruleForm.dynamicTags.push(inputValue)
      }
      this.inputValue = ''
    },
    removeDatasetTag(tag) {
      const index = this.ruleForm.dynamicTags.indexOf(tag)
      // indexOf 未命中返回 -1，splice(-1,1) 会误删最后一项
      if (index === -1) {
        return
      }
      this.ruleForm.dynamicTags.splice(index, 1)
    },
    triggerIconUpload() {
      const upload = this.$refs.iconUpload
      if (upload && upload.$refs['upload-inner']) {
        upload.$refs['upload-inner'].$refs.input.click()
      }
    },
    clearIcon() {
      this.ruleForm.icon = ''
      this.$emit('icon-uploaded')
    },
    customRequest(params) {
      this.uploadLoading = true
      const file = new FormData()
      file.append('file', params.file)
      uploadFile(file)
        .then((res) => {
          if (res.success) {
            this.ruleForm.icon = res.data
            this.$emit('icon-uploaded')
          }
        })
        .finally(() => {
          this.uploadLoading = false
        })
      return false
    },
    handleDomainChange(fromDomain) {
      if (fromDomain) {
        const matchedCdn = syncCdnFromDomain(this.ruleForm.domainName)
        if (matchedCdn) {
          this.ruleForm.cdnPrefix = matchedCdn
        }
      } else {
        const matchedDomain = syncDomainFromCdn(this.ruleForm.cdnPrefix)
        if (matchedDomain) {
          this.ruleForm.domainName = matchedDomain
        }
      }
    },
    addPackage() {
      this.ruleForm.packageList = [...(this.ruleForm.packageList || []), '']
    },
  },
}
</script>

<style scoped>
.appFormShell {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.formItemStyle {
  width: 100%;
}

.appIconUploadHidden {
  display: none;
}

.appFormShell__hint {
  margin: 0 0 12px;
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  line-height: 1.45;
}

.appFormShell >>> .linkFormBasic {
  align-items: flex-start;
}

.appFormShell >>> .linkFormBasic__media {
  display: flex;
  flex-direction: column;
  align-self: flex-start;
}

.appFormShell >>> .linkFormMediaAnchor {
  width: 200px;
  height: 200px;
  min-height: 200px;
  flex: none;
}

.appFormShell >>> .linkFormMediaAnchor__preview {
  width: 100%;
  height: 100%;
}

.appFormShell >>> .linkFormUploadZone--anchor {
  min-height: 200px;
}

.appFormShell >>> .linkFormBasic__fields.appFormShell__basicFields {
  align-self: stretch;
  min-width: 0;
  margin-top: 0;
}

.appFormShell >>> .appFormShell__nameField.el-form-item {
  height: auto;
  margin-bottom: 0;
}

.appFormShell >>> .appFormGrid .el-form-item {
  margin-bottom: 0;
}

.appFormShell >>> .appFormGrid .styleFormChoice {
  max-width: none;
}

.appFormShell >>> .linkFormBasic__urlField {
  display: none;
  margin: 0;
}

@media (max-width: 768px) {
  .appFormShell >>> .linkFormMediaAnchor {
    flex: none;
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }
}
</style>
